import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Coins, Flame, Settings, LogOut, Shield, Sparkles, History, Film, Download, Users, Store, Wallet, Plus } from 'lucide-react';
import NotificationBell from './NotificationBell';
import { useAuth } from '../../context/AuthContext';
import { usePwaInstall } from '../../lib/usePwaInstall';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';

export default function TopBar() {
  const { user, logout } = useAuth();
  const pwa = usePwaInstall();
  const nav = useNavigate();
  const initials = (user?.display_name || user?.username || 'JX').slice(0, 2).toUpperCase();

  return (
    <header data-testid="top-bar" className="sticky top-0 z-40 glass border-b border-white/10">
      <div className="flex items-center justify-between px-4 py-3 max-w-7xl mx-auto">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-xl gradient-brand flex items-center justify-center shadow-lg shadow-violet-500/30">
            <span className="font-display font-black text-white text-lg">J</span>
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-base">Jixxeo AI</div>
            <div className="text-[10px] text-slate-400 -mt-0.5">Ask. Create. Earn.</div>
          </div>
        </Link>

        <div className="flex items-center gap-2">
          {!pwa.installed && (pwa.installable || pwa.canPromptIOS) && (
            pwa.installable ? (
              <Button
                data-testid="topbar-install-btn"
                size="sm"
                onClick={async () => { await pwa.promptInstall(); }}
                className="hidden md:inline-flex gradient-brand text-white shadow-lg shadow-violet-500/30"
              >
                <Download className="w-3.5 h-3.5 mr-1" /> Install
              </Button>
            ) : (
              <Button
                data-testid="topbar-install-link"
                size="sm"
                variant="outline"
                onClick={() => nav('/install')}
                className="hidden md:inline-flex border-violet-500/40 text-violet-200 hover:bg-violet-500/10"
              >
                <Download className="w-3.5 h-3.5 mr-1" /> Install
              </Button>
            )
          )}
          {user && (
            <>
              <button
                data-testid="credits-balance-pill"
                onClick={() => nav('/credits')}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 text-slate-950 font-bold text-sm shadow-lg shadow-amber-500/20 hover:scale-105 transition-transform"
                title="Video credits — click to buy"
              >
                <Film className="w-3.5 h-3.5" />
                <span>{(user.credits_balance ?? 0).toLocaleString()}</span>
                <Plus data-testid="credits-plus-icon" className="w-3.5 h-3.5 opacity-80 -mr-0.5" />
              </button>
              <div data-testid="jxp-balance-display" className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full gradient-gold text-slate-950 font-bold text-sm shadow-lg shadow-amber-500/20">
                <Coins className="w-4 h-4" />
                <span>{user.jxp?.toLocaleString() || 0}</span>
              </div>
              <NotificationBell />
              <div data-testid="streak-display" className="hidden sm:flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-300 text-sm font-semibold">
                <Flame className="w-4 h-4" />
                <span>{user.streak || 0}</span>
              </div>
            </>
          )}
          {!user ? (
            <>
              <Button data-testid="login-btn" variant="ghost" size="sm" onClick={() => nav('/login')}>Sign in</Button>
              <Button data-testid="register-btn" size="sm" onClick={() => nav('/register')} className="gradient-brand text-white hover:opacity-90">Get started</Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="rounded-full ring-2 ring-violet-500/40 hover:ring-violet-500/70 transition-colors">
                  <Avatar className="w-9 h-9">
                    <AvatarImage src={user.avatar_url} />
                    <AvatarFallback className="gradient-brand text-white font-bold text-xs">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user.display_name}</div>
                  <div className="text-xs text-slate-400">@{user.username}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-profile" onClick={() => nav('/profile')}>
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-history" onClick={() => nav('/history')}>
                  <History className="w-4 h-4 mr-2" /> Chat history
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-recordings" onClick={() => nav('/recordings')}>
                  <Film className="w-4 h-4 mr-2" /> My recordings
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-search" onClick={() => nav('/search')}>
                  <Users className="w-4 h-4 mr-2" /> Find members
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-coinshop" onClick={() => nav('/coinshop')}>
                  <Store className="w-4 h-4 mr-2 text-amber-400" /> Coin Shop
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-credits" onClick={() => nav('/credits')}>
                  <Wallet className="w-4 h-4 mr-2 text-amber-300" /> Video Credits
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-wallet" onClick={() => nav('/wallet')}>
                  <Coins className="w-4 h-4 mr-2 text-amber-400" /> Wallet & Cash-out
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-marketplace" onClick={() => nav('/marketplace')}>
                  <Store className="w-4 h-4 mr-2 text-fuchsia-400" /> Marketplace
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-notification-settings" onClick={() => nav('/notifications/settings')}>
                  <Bell className="w-4 h-4 mr-2 text-violet-300" /> Notification Settings
                </DropdownMenuItem>
                {!pwa.installed && (
                  <DropdownMenuItem
                    data-testid="menu-install"
                    onClick={async () => {
                      if (pwa.installable) await pwa.promptInstall();
                      else nav('/install');
                    }}
                  >
                    <Download className="w-4 h-4 mr-2 text-violet-400" /> Install Jixxeo
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-upgrade" onClick={() => nav('/subscriptions')}>
                  <Sparkles className="w-4 h-4 mr-2 text-amber-400" /> Upgrade to Premium
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-settings" onClick={() => nav('/settings')}>
                  <Settings className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => nav('/admin')}>
                    <Shield className="w-4 h-4 mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); nav('/'); }}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
